import { useEffect, useCallback } from 'react';

interface UseFormAutoSaveOptions {
  formId: string;
  data: Record<string, any>;
  enabled?: boolean;
  interval?: number; // milliseconds
}

export function useFormAutoSave({
  formId,
  data,
  enabled = true,
  interval = 30000, // 30 seconds
}: UseFormAutoSaveOptions) {
  const saveKey = `hexaforms_draft_${formId}`;

  // Save to localStorage
  const saveDraft = useCallback(() => {
    if (!enabled || Object.keys(data).length === 0) return;
    
    try {
      const draft = {
        data,
        savedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
      };
      
      localStorage.setItem(saveKey, JSON.stringify(draft));
      
      // Debug mode
      if (typeof window !== 'undefined' && (window as any).HEXAFORMS_DEBUG) {
        console.log('[HexaForms] Draft saved:', draft);
      }
    } catch (error) {
      console.error('[HexaForms] Error saving draft:', error);
    }
  }, [formId, data, enabled, saveKey]);

  // Auto-save on interval
  useEffect(() => {
    if (!enabled) return;

    const intervalId = setInterval(saveDraft, interval);
    
    return () => clearInterval(intervalId);
  }, [saveDraft, interval, enabled]);

  // Save on unmount (tab close)
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = () => {
      saveDraft();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveDraft, enabled]);

  // Load saved draft
  const loadDraft = useCallback((): Record<string, any> | null => {
    try {
      const saved = localStorage.getItem(saveKey);
      if (!saved) return null;

      const draft = JSON.parse(saved);
      
      // Check if expired
      if (new Date(draft.expiresAt) < new Date()) {
        localStorage.removeItem(saveKey);
        return null;
      }

      return draft.data;
    } catch (error) {
      console.error('[HexaForms] Error loading draft:', error);
      return null;
    }
  }, [saveKey]);

  // Clear draft
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(saveKey);
    } catch (error) {
      console.error('[HexaForms] Error clearing draft:', error);
    }
  }, [saveKey]);

  return {
    saveDraft,
    loadDraft,
    clearDraft,
  };
}
